import React from "react";
import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useLang, useLocalePath } from "../contexts/LanguageContext";
import SEO, { organizationSchema } from "../components/SEO";

export default function Training() {
  const { t } = useLang();
  const localePath = useLocalePath();
  const levels = [
    { tag: t("training_page.l1_tag"), title: t("training_page.l1_title"), img: "/images/gallery/lsp-10.jpg", topics: t("training_page.l1_topics") || [] },
    { tag: t("training_page.l2_tag"), title: t("training_page.l2_title"), img: "/images/gallery/lsp-11.jpg", topics: t("training_page.l2_topics") || [] },
    { tag: t("training_page.l3_tag"), title: t("training_page.l3_title"), img: "/images/gallery/lsp-12.jpg", topics: t("training_page.l3_topics") || [] },
  ];

  return (
    <div>
      <SEO metaKey="training" pathname="/training" schemas={[organizationSchema()]} />

      <section className="relative isolate bg-forest text-white">
        <div className="absolute inset-0 z-0">
          <img src="/images/gallery/lsp-10.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/55 md:bg-forest/75" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40 min-h-[70vh] flex flex-col justify-end">
          <Reveal>
            <div className="editorial-divider mb-6 max-w-xs" style={{ color: "#E3A839" }}>{t("training_page.eyebrow")}</div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.96] max-w-5xl">
              {t("training_page.h1a")} <br /> <span className="italic text-mustard">{t("training_page.h1b")}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-white/85 leading-relaxed">{t("training_page.intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {levels.map((lv, i) => (
            <Reveal key={i} delay={i*0.08}>
              <div data-testid={`training-card-${i+1}`} className="relative group rounded-xl overflow-hidden border border-dust bg-kraft/20 h-full flex flex-col">
                <div className="h-56 overflow-hidden">
                  <img src={lv.img} alt={lv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="inline-flex w-fit px-3 py-1 text-[11px] tracking-[0.25em] uppercase rounded-full bg-mustard text-forest mb-4">{lv.tag}</div>
                  <h2 className="font-serif text-2xl text-forest">{lv.title}</h2>
                  <ul className="mt-5 space-y-2 flex-1">
                    {lv.topics.map((tp) => (
                      <li key={tp} className="flex items-center gap-2 text-ink/75">
                        <CheckCircle2 className="w-4 h-4 text-terracotta" /> {tp}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="kraft-texture">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="editorial-divider mb-4">{t("training_page.who_eyebrow")}</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-forest">{t("training_page.who_h2a")} <span className="italic text-terracotta">{t("training_page.who_h2b")}</span></h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {(t("training_page.audience") || []).map((a) => (
                <span key={a} className="px-5 py-3 rounded-full border border-forest/20 bg-ivory/60 text-forest">{a}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="editorial-divider mb-4">{t("training_page.modes_eyebrow")}</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-forest">{t("training_page.modes_h2a")} <span className="italic text-terracotta">{t("training_page.modes_h2b")}</span></h2>
            <ul className="mt-8 space-y-3">
              {(t("training_page.modes") || []).map((m) => (
                <li key={m} className="flex items-center gap-3 px-5 py-4 rounded-xl bg-ivory/70 border border-dust">
                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />
                  <span className="text-forest">{m}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest text-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h3 className="font-serif text-3xl lg:text-5xl max-w-2xl">{t("training_page.cta_title")}</h3>
          <Link to={localePath("/contact")} data-testid="training-cta-book" className="btn-pill bg-mustard text-forest hover:bg-mustard/90 whitespace-nowrap">
            {t("training_page.cta_btn")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
